'use client';

import { c as _c } from "react/compiler-runtime";
import { signInPath } from '@district-sdk/auth-nextjs/client';
import { MarketplaceId as SdkMarketplaceId } from '@district-sdk/zod-schema/district';
import { useEffect, useRef, useState } from 'react';
import { logger } from '@district-core/logger';
import { request } from '@isoapp/common/platform/networking/client/request';
import { Id } from '@isoapp/common/platform/types/scalars/scalars';
import { showPopupMessage, watchConnectPopup, withTimeout } from '#@/components/utils/connectPopup';
import { graphql } from '#@graphql';
const log = logger('shopify', 'connect');
const MARKETPLACE_LOOKUP_TIMEOUT_MS = 15_000;
export function useConnectToDistrict() {
  const $ = _c(10);
  const [marketplaceSlug, setMarketplaceSlug] = useState("");
  const [marketplaceSlugError, setMarketplaceSlugError] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const cleanupRef = useRef(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => () => {
      cleanupRef.current?.();
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function changeSlug(value) {
      setMarketplaceSlug(value);
      setMarketplaceSlugError(null);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const changeSlug = t2;
  let t3;
  if ($[3] !== marketplaceSlug) {
    t3 = async function connect() {
      const slug = marketplaceSlug.trim();
      if (!slug) {
        setMarketplaceSlugError("Enter your marketplace slug.");
        return;
      }
      setMarketplaceSlugError(null);
      setIsConnecting(true);
      const openedPopup = window.open("about:blank", "_blank");
      if (!openedPopup) {
        setMarketplaceSlugError("Enable popups for this site, then try again.");
        setIsConnecting(false);
        return;
      }
      const popup = openedPopup;
      showPopupMessage(popup, "Redirecting\u2026");
      const targetOrigin = globalThis.location.origin;
      const mintAndSendToken = async function mintAndSendToken() {
        ;
        try {
          const sessionToken = await mintSessionToken();
          if (!sessionToken) {
            cleanup();
            popup.close();
            setMarketplaceSlugError("Could not retrieve a session token. Please try again.");
            setIsConnecting(false);
            return;
          }
          popup.postMessage({
            type: "district:shopify-session-token",
            sessionToken
          }, targetOrigin);
        } catch (t4) {
          const error = t4;
          log.error("failed to send Shopify session token to popup", {
            error
          });
          cleanup();
          setMarketplaceSlugError("Something went wrong. Please try again.");
          setIsConnecting(false);
        }
      };
      const stopWatching = watchConnectPopup(popup, targetOrigin, {
        onClosed() {
          cleanup();
          setIsConnecting(false);
        },
        onAuthReady() {
          mintAndSendToken();
        },
        onConnected() {
          cleanup();
          if (typeof shopify !== "undefined") {
            shopify.toast.show("Account connected successfully");
          }
          globalThis.location.reload();
        },
        onFailed(message) {
          cleanup();
          showPopupMessage(popup, "Something went wrong. You can close this window.");
          setMarketplaceSlugError(message ?? "Something went wrong. Please try again.");
          setIsConnecting(false);
        }
      });
      function cleanup() {
        stopWatching();
        cleanupRef.current = null;
      }
      cleanupRef.current = cleanup;
      ;
      try {
        const signInUrl = await resolveSignInUrl(slug);
        if (!signInUrl) {
          cleanup();
          showPopupMessage(popup, "We couldn't find a marketplace with that URL. You can close this window.");
          setMarketplaceSlugError("We couldn't find a marketplace with that URL.");
          setIsConnecting(false);
          return;
        }
        popup.location.href = signInUrl;
      } catch (t5) {
        const error_0 = t5;
        log.error("failed to resolve marketplace for Shopify connect", {
          error: error_0
        });
        cleanup();
        showPopupMessage(popup, "Something went wrong. You can close this window.");
        setMarketplaceSlugError("Something went wrong. Please try again.");
        setIsConnecting(false);
      }
    };
    $[3] = marketplaceSlug;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const connect = t3;
  let t4;
  if ($[5] !== connect || $[6] !== isConnecting || $[7] !== marketplaceSlug || $[8] !== marketplaceSlugError) {
    t4 = {
      marketplaceSlug,
      marketplaceSlugError,
      isConnecting,
      changeSlug,
      connect
    };
    $[5] = connect;
    $[6] = isConnecting;
    $[7] = marketplaceSlug;
    $[8] = marketplaceSlugError;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
async function mintSessionToken(): Promise<string | null> {
  if (typeof shopify === 'undefined') {
    return null;
  }
  try {
    return await shopify.idToken();
  } catch (error) {
    log.error('failed to retrieve Shopify session token', {
      error
    });
    return null;
  }
}

/** Returns the popup sign-in URL for a matching marketplace slug, or `null`. */
async function resolveSignInUrl(slug: string): Promise<string | null> {
  const {
    marketplace
  } = await withTimeout(request.auth(ShopifyLinkMarketplace_Query, {
    id: Id.parse(slug)
  }), MARKETPLACE_LOOKUP_TIMEOUT_MS);
  if (!marketplace) {
    return null;
  }
  return signInPath({
    marketplaceId: SdkMarketplaceId.parse(marketplace.id.startsWith('mp_') ? marketplace.id : `mp_${marketplace.id}`),
    redirect: '/connect/finish'
  });
}
const ShopifyLinkMarketplace_Query = graphql(`
  query ShopifyLinkMarketplace($id: ID!) {
    marketplace(id: $id) {
      id
    }
  }
`);